import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../services/api';

interface CalendarEntry {
  id: number;
  project_id: number;
  sop_map_id: number | null;
  sop_topic_id: number | null;
  article_id: number | null;
  cw_non_bn_article_id?: number | null;
  source_type?: string | null;
  title: string;
  keyword: string;
  scheduled_date: string | null;
  status: string;
  category: string;
  priority: string;
  notes: string;
  article_title?: string | null;
  article_word_count?: number | null;
  article_wp_url?: string | null;
  article_published_at?: string | null;
  article_slug?: string | null;
  article_wp_post_id?: number | null;
  article_wp_scheduled_date?: string | null;
  article_wp_status?: string | null;
  article_publish_mode?: string | null;
  article_status?: string | null;
}

const STATUS_OPTIONS = [
  'scheduled', 'rewrite', 'writing', 'written', 'images', 'ready', 'published',
];

const STATUS_COLORS: Record<string, string> = {
  scheduled: 'bg-blue-100 text-blue-800',
  rewrite: 'bg-orange-100 text-orange-800',
  writing: 'bg-yellow-100 text-yellow-800',
  written: 'bg-purple-100 text-purple-800',
  images: 'bg-indigo-100 text-indigo-800',
  ready: 'bg-teal-100 text-teal-800',
  published: 'bg-green-100 text-green-800',
};

const ContentCalendarPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<CalendarEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [filter, setFilter] = useState<string>('all'); // status filter
  const [monthFilter, setMonthFilter] = useState<string>(''); // YYYY-MM
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [error, setError] = useState('');

  // Publish/Schedule modal
  const [pubModalOpen, setPubModalOpen] = useState(false);
  const [pubEntry, setPubEntry] = useState<CalendarEntry | null>(null);
  const [pubPostStatus, setPubPostStatus] = useState<'publish' | 'future' | 'draft'>('publish');
  const [pubScheduledDate, setPubScheduledDate] = useState<string>(''); // YYYY-MM-DDTHH:mm
  const [pubMode, setPubMode] = useState<'standard_post' | 'acf_post' | 'hierarchical_page'>('standard_post');
  const [pubCptSlug, setPubCptSlug] = useState('');
  const [pubSlug, setPubSlug] = useState('');
  const [pubUseGdrive, setPubUseGdrive] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishingEntryId, setPublishingEntryId] = useState<number | null>(null); // for one-click Publish Now spinner
  const [revertingEntryId, setRevertingEntryId] = useState<number | null>(null); // for Revert button spinner

  // Bulk Schedule/Publish modal
  const [bulkPubModalOpen, setBulkPubModalOpen] = useState(false);
  const [bulkPubStatus, setBulkPubStatus] = useState<'publish' | 'future' | 'draft'>('future');
  const [bulkPubMode, setBulkPubMode] = useState<'auto' | 'standard_post' | 'acf_post' | 'hierarchical_page'>('auto');
  const [bulkPubCptSlug, setBulkPubCptSlug] = useState('');
  const [bulkPubBaseTime, setBulkPubBaseTime] = useState('09:00');
  const [bulkPubStagger, setBulkPubStagger] = useState<number>(0);
  const [bulkPubUseGdrive, setBulkPubUseGdrive] = useState(false);
  const [bulkPubAuthorId, setBulkPubAuthorId] = useState<number | ''>('');
  const [bulkPubSaving, setBulkPubSaving] = useState(false);

  // Author state (WP users) — shared between single + bulk modals
  const [pubAuthorId, setPubAuthorId] = useState<number | ''>('');
  const [wpAuthors, setWpAuthors] = useState<Array<{ id: number; name: string; slug: string; email?: string }>>([]);
  const [wpAuthorsCurrentId, setWpAuthorsCurrentId] = useState<number | null>(null);

  // WP credential + Google Drive status
  const [wpStatus, setWpStatus] = useState<{ exists: boolean; is_verified: boolean; site_url: string | null } | null>(null);
  const [gdriveStatus, setGdriveStatus] = useState<{ configured: boolean; folder_accessible: boolean; project_enabled: boolean } | null>(null);

  // Monthly report modal
  interface ReportEntry {
    entry_id: number;
    article_id: number | null;
    title: string;
    keyword: string;
    category: string;
    publish_mode: string;
    wp_post_id: number | null;
    wp_url: string;
    wp_status: string;
    published_at: string;
    scheduled_date: string | null;
  }
  interface ReportBucket { month: string; count: number; entries: ReportEntry[]; }
  interface ReportData {
    project_id: number;
    project_name: string;
    scope: { month: string | null };
    total_published: number;
    months: ReportBucket[];
  }
  const [reportOpen, setReportOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [reportMonth, setReportMonth] = useState<string>(''); // YYYY-MM or blank = all
  const [reportError, setReportError] = useState('');

  const loadReport = useCallback(async () => {
    if (!projectId) return;
    setReportLoading(true);
    setReportError('');
    try {
      const qs = reportMonth ? `?month=${reportMonth}` : '';
      const r = await api.get(`/content-calendar/report/${projectId}${qs}`);
      setReportData(r.data as ReportData);
    } catch (e: any) {
      setReportError(e.response?.data?.detail || 'Failed to load report');
    } finally {
      setReportLoading(false);
    }
  }, [projectId, reportMonth]);

  const openReportModal = () => {
    setReportOpen(true);
    // Fire a fresh load whenever opened
    setTimeout(() => loadReport(), 0);
  };

  const downloadReportCsv = () => {
    if (!projectId) return;
    const token = localStorage.getItem('auth_token') || '';
    const params = new URLSearchParams();
    if (reportMonth) params.set('month', reportMonth);
    if (token) params.set('token', token);
    const qs = params.toString();
    const url = `/api/content-calendar/report/${projectId}/csv${qs ? `?${qs}` : ''}`;
    window.open(url, '_blank');
  };

  // ── Slack integration ──
  const [slackOpen, setSlackOpen] = useState(false);
  const [slackConfigured, setSlackConfigured] = useState<boolean>(false);
  const [slackPreview, setSlackPreview] = useState<string>('');
  const [slackWebhookInput, setSlackWebhookInput] = useState<string>('');
  const [slackSaving, setSlackSaving] = useState<boolean>(false);
  const [slackTesting, setSlackTesting] = useState<boolean>(false);
  const [slackMessage, setSlackMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const loadSlackConfig = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await api.get(`/content-calendar/slack-config/${projectId}`);
      setSlackConfigured(!!r.data.configured);
      setSlackPreview(r.data.preview || '');
    } catch {
      setSlackConfigured(false);
      setSlackPreview('');
    }
  }, [projectId]);

  useEffect(() => { loadSlackConfig(); }, [loadSlackConfig]);

  const openSlackModal = () => {
    setSlackWebhookInput('');
    setSlackMessage(null);
    setSlackOpen(true);
  };

  const saveSlackConfig = async (clear: boolean = false) => {
    if (!projectId) return;
    setSlackSaving(true);
    setSlackMessage(null);
    try {
      const url = clear ? '' : slackWebhookInput.trim();
      const r = await api.put(`/content-calendar/slack-config/${projectId}`, { webhook_url: url });
      setSlackConfigured(!!r.data.configured);
      setSlackPreview(r.data.preview || '');
      setSlackMessage({ kind: 'ok', text: clear ? 'Slack webhook cleared.' : 'Slack webhook saved.' });
      if (!clear) setSlackWebhookInput('');
    } catch (e: any) {
      setSlackMessage({ kind: 'err', text: e.response?.data?.detail || 'Failed to save' });
    } finally {
      setSlackSaving(false);
    }
  };

  const testSlackConfig = async () => {
    if (!projectId) return;
    setSlackTesting(true);
    setSlackMessage(null);
    try {
      await api.post(`/content-calendar/slack-config/${projectId}/test`, {});
      setSlackMessage({ kind: 'ok', text: 'Test message sent — check your Slack channel.' });
    } catch (e: any) {
      setSlackMessage({ kind: 'err', text: e.response?.data?.detail || 'Test failed' });
    } finally {
      setSlackTesting(false);
    }
  };

  const loadProjectName = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${projectId}`);
      setProjectName(r.data.name || 'Project');
    } catch {}
  }, [projectId]);

  const loadEntries = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const qs = monthFilter ? `?month=${monthFilter}` : '';
      const r = await api.get(`/content-calendar/entries/${projectId}${qs}`);
      setEntries(r.data.entries || []);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [projectId, monthFilter]);

  const loadWpStatus = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await api.get(`/content-calendar/wp-status/${projectId}`);
      setWpStatus(r.data);
    } catch {
      setWpStatus(null);
    }
  }, [projectId]);

  const loadGdriveStatus = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await api.get(`/content-calendar/gdrive-status/${projectId}`);
      setGdriveStatus(r.data);
    } catch {
      setGdriveStatus(null);
    }
  }, [projectId]);

  const loadWpAuthors = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await api.get(`/content-calendar/wp-authors/${projectId}`);
      setWpAuthors(r.data?.authors || []);
      setWpAuthorsCurrentId(r.data?.current_user_id ?? null);
    } catch {
      setWpAuthors([]);
      setWpAuthorsCurrentId(null);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      loadProjectName();
      loadEntries();
      loadWpStatus();
      loadGdriveStatus();
      loadWpAuthors();
    }
  }, [projectId, loadProjectName, loadEntries, loadWpStatus, loadGdriveStatus, loadWpAuthors]);

  const wpMissing = wpStatus !== null && !wpStatus.exists;
  const gdriveAvailable = !!gdriveStatus?.configured && !!gdriveStatus?.folder_accessible;

  const handleStatusChange = async (entryId: number, status: string) => {
    try {
      await api.put(`/content-calendar/entries/${entryId}`, { status });
      loadEntries();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async (entryId: number) => {
    if (!window.confirm('Delete this calendar entry?')) return;
    try {
      await api.delete(`/content-calendar/entries/${entryId}`);
      loadEntries();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected entries?`)) return;
    try {
      await api.post(`/content-calendar/entries/bulk-delete/${projectId}`, {
        ids: Array.from(selectedIds),
      });
      setSelectedIds(new Set());
      loadEntries();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleSelect = (id: number) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };

  const openBulkPublishModal = () => {
    if (selectedIds.size === 0) return;
    if (wpMissing) {
      setError('WordPress credentials are missing for this project. Configure them in LiveUpdate → Credentials before scheduling.');
      return;
    }
    setBulkPubStatus('future');
    setBulkPubMode('auto');
    setBulkPubCptSlug('');
    setBulkPubBaseTime('09:00');
    setBulkPubStagger(0);
    setBulkPubUseGdrive(!!gdriveStatus?.project_enabled);
    setBulkPubAuthorId(wpAuthorsCurrentId ?? '');
    setBulkPubModalOpen(true);
  };

  const handleBulkPublish = async () => {
    if (selectedIds.size === 0) return;
    // Filter to only eligible entries (linked + not published)
    const eligible = entries.filter(e => selectedIds.has(e.id) && (e.article_id || e.cw_non_bn_article_id) && e.status !== 'published');
    if (eligible.length === 0) {
      alert('None of the selected entries are eligible (need linked article + not already published).');
      return;
    }
    if (bulkPubMode === 'acf_post' && !bulkPubCptSlug) {
      alert('Please enter a CPT slug for ACF Custom Post mode.');
      return;
    }
    setBulkPubSaving(true);
    setError('');
    try {
      const res = await api.post('/content-calendar/entries/bulk-publish', {
        entry_ids: eligible.map(e => e.id),
        post_status: bulkPubStatus,
        publish_mode: bulkPubMode,
        cpt_slug: bulkPubCptSlug,
        base_time: bulkPubBaseTime,
        stagger_minutes: bulkPubStagger,
        use_gdrive_images: bulkPubUseGdrive,
        author_id: bulkPubAuthorId === '' ? null : bulkPubAuthorId,
      });
      const { succeeded = [], failed = [], total = 0 } = res.data || {};
      setBulkPubModalOpen(false);
      setSelectedIds(new Set());
      let msg = `${succeeded.length} of ${total} succeeded.`;
      if (failed.length > 0) {
        const lines = failed.slice(0, 5).map((f: any) => `  • entry ${f.entry_id}: ${f.error}`).join('\n');
        msg += `\n\n${failed.length} failed:\n${lines}`;
        if (failed.length > 5) msg += `\n  ...and ${failed.length - 5} more`;
      }
      alert(msg);
      loadEntries();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Bulk publish failed');
    }
    setBulkPubSaving(false);
  };

  const openScheduleModal = (entry: CalendarEntry) => {
    if (wpMissing) {
      setError('WordPress credentials are missing for this project. Configure them in LiveUpdate → Credentials before scheduling.');
      return;
    }
    setPubEntry(entry);
    setPubPostStatus('future');
    // Default scheduled time = entry.scheduled_date at 09:00 (or today if null)
    const baseDate = entry.scheduled_date || new Date().toISOString().slice(0, 10);
    setPubScheduledDate(`${baseDate}T09:00`);
    setPubMode('standard_post');
    setPubCptSlug('');
    setPubSlug('');
    setPubUseGdrive(!!gdriveStatus?.project_enabled);
    setPubAuthorId(wpAuthorsCurrentId ?? '');
    setPubModalOpen(true);
  };

  const publishNow = async (entry: CalendarEntry) => {
    if (!entry.article_id && !entry.cw_non_bn_article_id) {
      setError('This entry has no linked article yet — finish generating it first.');
      return;
    }
    if (wpMissing) {
      setError('WordPress credentials are missing for this project. Configure them in LiveUpdate → Credentials before publishing.');
      return;
    }
    if (!confirm(`Publish "${entry.title || entry.keyword}" to WordPress now?`)) return;
    setPublishingEntryId(entry.id);
    setError('');
    try {
      await api.post(`/content-calendar/entries/${entry.id}/publish`, {
        publish_mode: 'standard_post',
        post_status: 'publish',
      });
      await loadEntries();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Publish failed');
    }
    setPublishingEntryId(null);
  };

  const revertSchedule = async (entry: CalendarEntry) => {
    if (!entry.article_id && !entry.cw_non_bn_article_id) return;
    if (!confirm(`Permanently delete the WordPress post for "${entry.title || entry.keyword}"?\n\nThis removes the post from WordPress entirely (scheduled or draft) and resets this entry so you can re-schedule or re-publish a fresh copy.`)) return;
    setRevertingEntryId(entry.id);
    setError('');
    try {
      await api.post(`/content-calendar/entries/${entry.id}/revert-schedule`);
      await loadEntries();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Revert failed');
    }
    setRevertingEntryId(null);
  };

  const handleSchedulePublish = async () => {
    if (!pubEntry) return;
    if (pubPostStatus === 'future' && !pubScheduledDate) {
      setError('Please pick a scheduled date/time');
      return;
    }
    setPublishing(true);
    setError('');
    try {
      // Convert datetime-local to ISO 8601 (append :00 seconds)
      const scheduledIso = pubScheduledDate ? `${pubScheduledDate}:00` : '';
      await api.post(`/content-calendar/entries/${pubEntry.id}/publish`, {
        publish_mode: pubMode,
        post_status: pubPostStatus,
        cpt_slug: pubCptSlug,
        scheduled_date: scheduledIso,
        slug: pubSlug,
        use_gdrive_images: pubUseGdrive,
        author_id: pubAuthorId === '' ? null : pubAuthorId,
      });
      setPubModalOpen(false);
      await loadEntries();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Publish failed');
    }
    setPublishing(false);
  };

  const openInContentWriter = (entry: CalendarEntry) => {
    const params = new URLSearchParams();
    if (entry.keyword) params.set('keyword', entry.keyword);
    if (entry.title) params.set('title', entry.title);
    params.set('calendar_entry_id', String(entry.id));
    // Extract source URL from notes if present
    const m = entry.notes?.match(/Source URL:\s*(\S+)/);
    if (m) params.set('source_url', m[1]);
    const targetPage = entry.source_type === 'cw_non_bn' ? 'cw-non-bn' : 'content-writer';
    navigate(`/projects/${projectId}/${targetPage}?${params.toString()}`);
  };

  // Apply month filter first — stats + table + select-all must all see the same
  // set. `monthFilter` is YYYY-MM. An entry matches if its scheduled_date
  // falls in that month. Entries without a scheduled_date are excluded when
  // a month filter is active.
  const monthScoped = monthFilter
    ? entries.filter(e => {
        if (!e.scheduled_date) return false;
        return String(e.scheduled_date).startsWith(monthFilter);
      })
    : entries;

  // Status filter applied on top of month scope
  const filtered = monthScoped.filter(e => filter === 'all' || e.status === filter);

  // Stats — computed against the month-scoped set so cards update live
  // when the month changes. Status filter is NOT applied here so every
  // card still shows its own per-status count for the current month.
  const stats: Record<string, number> = {};
  monthScoped.forEach(e => { stats[e.status] = (stats[e.status] || 0) + 1; });

  return (
    <Layout projectId={parseInt(projectId || '0')} projectName={projectName}>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Content Calendar</h1>
              <p className="text-gray-600">
                Schedule, track, and publish content across all sources (Topical Map, Website Audit).
              </p>
            </div>
            <a
              href="https://drive.google.com/file/d/1WEjp577XcDXXYTnn-8-nzkP4VdYP0-yE/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors"
              title="Watch tutorial video"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
              Watch Tutorial
            </a>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {error}
          </div>
        )}

        {/* WordPress credentials banner */}
        {wpMissing && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-300 rounded-lg flex items-start justify-between gap-3">
            <div className="text-sm text-yellow-900">
              <div className="font-semibold mb-0.5">WordPress credentials are missing.</div>
              <div className="text-yellow-800">
                Publishing and scheduling are disabled until WP credentials are saved for this project.
              </div>
            </div>
            <button
              onClick={() => navigate(`/projects/${projectId}/liveupdate/credentials`)}
              className="shrink-0 px-3 py-1.5 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md"
            >
              Configure now
            </button>
          </div>
        )}

        {/* Google Drive image-hosting default toggle */}
        {gdriveStatus && gdriveStatus.configured && gdriveStatus.folder_accessible && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between gap-3">
            <div className="text-sm text-gray-700">
              <span className="font-medium">Google Drive image hosting:</span>{' '}
              <span className={gdriveStatus.project_enabled ? 'text-green-700' : 'text-gray-500'}>
                {gdriveStatus.project_enabled ? 'Enabled by default' : 'Disabled by default'}
              </span>
              <span className="text-xs text-gray-500 ml-2">
                Body images will be hosted on Drive (lh3 CDN) instead of WP Media.
              </span>
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={!!gdriveStatus.project_enabled}
                onChange={async (e) => {
                  try {
                    await api.patch(`/content-calendar/projects/${projectId}/gdrive-images`, {
                      enabled: e.target.checked,
                    });
                    setGdriveStatus({ ...gdriveStatus, project_enabled: e.target.checked });
                  } catch (err: any) {
                    setError(err.response?.data?.detail || 'Failed to update gdrive preference');
                  }
                }}
                className="h-4 w-4"
              />
              Use by default
            </label>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`p-3 rounded-lg border text-left transition-colors ${filter === 'all' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
          >
            <div className="text-xs text-gray-500 uppercase">Total</div>
            <div className="text-2xl font-bold text-gray-900">{monthScoped.length}</div>
          </button>
          {STATUS_OPTIONS.map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`p-3 rounded-lg border text-left transition-colors ${filter === s ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
            >
              <div className="text-xs text-gray-500 uppercase capitalize">{s}</div>
              <div className="text-2xl font-bold text-gray-900">{stats[s] || 0}</div>
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4 flex flex-wrap gap-3 items-center justify-between">
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-600 flex items-center gap-2">
              Month:
              <input
                type="month"
                value={monthFilter}
                onChange={e => setMonthFilter(e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded-md text-sm"
              />
            </label>
            {monthFilter && (
              <button
                onClick={() => setMonthFilter('')}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <>
                <button
                  onClick={openBulkPublishModal}
                  disabled={wpMissing}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  title={wpMissing ? 'Configure WordPress credentials first' : 'Publish or schedule selected entries to WordPress'}
                >
                  Schedule/Publish ({selectedIds.size})
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                >
                  Delete ({selectedIds.size})
                </button>
              </>
            )}
            <button
              onClick={openReportModal}
              className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
              title="View a monthly publishing report"
            >
              Monthly Report
            </button>
            <button
              onClick={openSlackModal}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${slackConfigured ? 'text-white bg-purple-600 hover:bg-purple-700' : 'text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300'}`}
              title={slackConfigured ? `Slack ${slackPreview}` : 'Configure Slack notifications'}
            >
              {slackConfigured ? 'Slack: On' : 'Slack: Off'}
            </button>
            <button
              onClick={loadEntries}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No calendar entries yet. Add topics from Topical Map or send from Website Audit.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.size > 0 && selectedIds.size === filtered.length}
                        onChange={e => {
                          if (e.target.checked) setSelectedIds(new Set(filtered.map(x => x.id)));
                          else setSelectedIds(new Set());
                        }}
                      />
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Keyword</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">WP Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filtered.map(e => (
                    <tr key={e.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(e.id)}
                          onChange={() => toggleSelect(e.id)}
                        />
                      </td>
                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                        {e.scheduled_date ? new Date(e.scheduled_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-3 py-2 text-gray-900 max-w-md truncate" title={e.title}>
                        {e.article_wp_url ? (
                          <a href={e.article_wp_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            {e.title || '-'}
                          </a>
                        ) : (e.title || '-')}
                      </td>
                      <td className="px-3 py-2 text-gray-600 max-w-xs truncate" title={e.keyword}>
                        {e.keyword || '-'}
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={e.status}
                          onChange={ev => handleStatusChange(e.id, ev.target.value)}
                          className={`text-xs font-medium px-2 py-1 rounded-full border-0 ${STATUS_COLORS[e.status] || 'bg-gray-100 text-gray-700'}`}
                        >
                          {STATUS_OPTIONS.map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {e.article_wp_url || e.article_wp_scheduled_date || e.article_published_at ? (
                          <div className="flex flex-col gap-0.5">
                            {e.article_wp_status === 'publish' || e.article_published_at ? (
                              <span className="inline-flex items-center gap-1 text-green-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                Live
                                {e.article_published_at && (
                                  <span className="text-gray-500">
                                    · {new Date(e.article_published_at).toLocaleDateString()}
                                  </span>
                                )}
                              </span>
                            ) : e.article_wp_status === 'future' || e.article_wp_scheduled_date ? (
                              <span className="inline-flex items-center gap-1 text-teal-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-teal-500"></span>
                                Scheduled
                                {e.article_wp_scheduled_date && (
                                  <span className="text-gray-500">
                                    · {new Date(e.article_wp_scheduled_date).toLocaleString([], {
                                      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                                    })}
                                  </span>
                                )}
                              </span>
                            ) : (
                              <span className="text-gray-500">Draft</span>
                            )}
                            {e.article_wp_url && (
                              <a
                                href={e.article_wp_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline truncate max-w-[200px]"
                                title={e.article_wp_url}
                              >
                                {e.article_wp_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                              </a>
                            )}
                            {e.article_publish_mode && (
                              <span className="text-[10px] text-gray-400 uppercase">
                                {e.article_publish_mode.replace('_', ' ')}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{e.category || '-'}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs capitalize">{e.priority || 'medium'}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => openInContentWriter(e)}
                            className="text-xs px-2 py-1 font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded"
                            title="Open in Content Writer"
                          >
                            Write
                          </button>
                          {(e.article_id || e.cw_non_bn_article_id) && e.status !== 'published' && (
                            <>
                              <button
                                onClick={() => publishNow(e)}
                                disabled={publishingEntryId === e.id || wpMissing}
                                className="text-xs px-2 py-1 font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                title={wpMissing ? 'Configure WordPress credentials first' : 'Publish to WordPress now'}
                              >
                                {publishingEntryId === e.id ? 'Publishing…' : 'Publish'}
                              </button>
                              <button
                                onClick={() => openScheduleModal(e)}
                                disabled={wpMissing}
                                className="text-xs px-2 py-1 font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                title={wpMissing ? 'Configure WordPress credentials first' : 'Schedule publish to WordPress'}
                              >
                                Schedule
                              </button>
                              {!!e.article_wp_post_id && e.article_wp_status !== 'publish' && !e.article_published_at && (
                                <button
                                  onClick={() => revertSchedule(e)}
                                  disabled={revertingEntryId === e.id}
                                  className="text-xs px-2 py-1 font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Delete the WordPress post entirely (scheduled or draft) and reset this entry"
                                >
                                  {revertingEntryId === e.id ? 'Removing…' : 'Revert'}
                                </button>
                              )}
                            </>
                          )}
                          <button
                            onClick={() => handleDelete(e.id)}
                            className="text-xs px-2 py-1 font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded"
                            title="Delete entry"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ═══════════ SCHEDULE / PUBLISH MODAL ═══════════ */}
      {pubModalOpen && pubEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Publish to WordPress</h3>
            <p className="text-xs text-gray-500 mb-4 truncate">{pubEntry.title || pubEntry.keyword}</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Post Status *</label>
                <select
                  value={pubPostStatus}
                  onChange={e => setPubPostStatus(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="publish">Publish Now</option>
                  <option value="future">Schedule (WordPress will auto-publish)</option>
                  <option value="draft">Draft</option>
                </select>
              </div>

              {pubPostStatus === 'future' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date/Time *</label>
                  <input
                    type="datetime-local"
                    value={pubScheduledDate}
                    onChange={e => setPubScheduledDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Defaults to this entry's scheduled date at 09:00. WordPress will publish automatically.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Publish Mode</label>
                <select
                  value={pubMode}
                  onChange={e => setPubMode(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="standard_post">Standard Post</option>
                  <option value="acf_post">Custom Post Type (ACF)</option>
                  <option value="hierarchical_page">Hierarchical Page</option>
                </select>
              </div>

              {pubMode === 'acf_post' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CPT Slug *</label>
                  <input
                    type="text"
                    value={pubCptSlug}
                    onChange={e => setPubCptSlug(e.target.value)}
                    placeholder="e.g. services, locations"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Custom Slug <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={pubSlug}
                  onChange={e => setPubSlug(e.target.value)}
                  placeholder="Leave blank to use article's existing slug"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Author</label>
                <select
                  value={pubAuthorId}
                  onChange={e => setPubAuthorId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">WordPress default (the credential user)</option>
                  {wpAuthors.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name}{a.slug ? ` (${a.slug})` : ''}{a.id === wpAuthorsCurrentId ? ' — current user' : ''}
                    </option>
                  ))}
                </select>
                {wpAuthors.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No WP users loaded. Check credentials / REST access.</p>
                )}
              </div>

              {gdriveAvailable && (
                <label className="flex items-start gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pubUseGdrive}
                    onChange={e => setPubUseGdrive(e.target.checked)}
                    className="h-4 w-4 mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-700">Host body images on Google Drive (lh3 links)</div>
                    <div className="text-xs text-gray-500">Skip WP Media for body images; use Drive's public CDN.</div>
                  </div>
                </label>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setPubModalOpen(false)}
                disabled={publishing}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSchedulePublish}
                disabled={publishing || (pubPostStatus === 'future' && !pubScheduledDate) || (pubMode === 'acf_post' && !pubCptSlug)}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50"
              >
                {publishing ? 'Publishing…' : pubPostStatus === 'future' ? 'Schedule' : pubPostStatus === 'publish' ? 'Publish' : 'Save Draft'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ BULK SCHEDULE/PUBLISH MODAL ═══════════ */}
      {bulkPubModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Bulk Schedule/Publish to WordPress</h3>
            <p className="text-xs text-gray-500 mb-4">
              {selectedIds.size} entries selected. Only entries with a finished article and not yet published will be processed.
              {bulkPubStatus === 'future' && ' Each post uses its calendar-entry date combined with the base time below.'}
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Post Status</label>
                <select
                  value={bulkPubStatus}
                  onChange={e => setBulkPubStatus(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="publish">Publish now (ignores dates)</option>
                  <option value="future">Schedule (uses each entry's date)</option>
                  <option value="draft">Save as draft</option>
                </select>
              </div>
              {bulkPubStatus === 'future' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Time</label>
                    <input
                      type="time"
                      value={bulkPubBaseTime}
                      onChange={e => setBulkPubBaseTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stagger (min)</label>
                    <input
                      type="number"
                      min={0}
                      value={bulkPubStagger}
                      onChange={e => setBulkPubStagger(Math.max(0, Number(e.target.value) || 0))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      title="Minutes added between posts sharing the same date"
                    />
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Publish Mode</label>
                <select
                  value={bulkPubMode}
                  onChange={e => setBulkPubMode(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="auto">Auto (detect from slug)</option>
                  <option value="standard_post">Standard Post</option>
                  <option value="hierarchical_page">Page (Hierarchical)</option>
                  <option value="acf_post">ACF Custom Post</option>
                </select>
                {bulkPubMode === 'auto' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Articles with a slug containing "/" (e.g. addiction/drug/marijuana/x) become
                    hierarchical pages; others become standard posts.
                  </p>
                )}
              </div>
              {bulkPubMode === 'acf_post' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CPT Slug *</label>
                  <input
                    type="text"
                    value={bulkPubCptSlug}
                    onChange={e => setBulkPubCptSlug(e.target.value)}
                    placeholder="e.g. services, locations"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Author</label>
                <select
                  value={bulkPubAuthorId}
                  onChange={e => setBulkPubAuthorId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">WordPress default (the credential user)</option>
                  {wpAuthors.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name}{a.slug ? ` (${a.slug})` : ''}{a.id === wpAuthorsCurrentId ? ' — current user' : ''}
                    </option>
                  ))}
                </select>
                {wpAuthors.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No WP users loaded. Check credentials / REST access.</p>
                )}
              </div>
              {gdriveAvailable && (
                <label className="flex items-start gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bulkPubUseGdrive}
                    onChange={e => setBulkPubUseGdrive(e.target.checked)}
                    className="h-4 w-4 mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-700">Host body images on Google Drive (lh3 links)</div>
                    <div className="text-xs text-gray-500">Applies to all entries in this batch.</div>
                  </div>
                </label>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setBulkPubModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                disabled={bulkPubSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkPublish}
                disabled={bulkPubSaving || (bulkPubMode === 'acf_post' && !bulkPubCptSlug)}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50"
              >
                {bulkPubSaving ? 'Processing…' : bulkPubStatus === 'future' ? `Schedule ${selectedIds.size}` : bulkPubStatus === 'publish' ? `Publish ${selectedIds.size}` : `Draft ${selectedIds.size}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Report modal */}
      {reportOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Monthly Publishing Report</h3>
                <p className="text-xs text-gray-500">{projectName} — published articles grouped by month</p>
              </div>
              <button
                onClick={() => setReportOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap items-center gap-3">
              <label className="text-sm text-gray-700">Month:</label>
              <input
                type="month"
                value={reportMonth}
                onChange={e => setReportMonth(e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded-md text-sm"
              />
              <button
                onClick={() => loadReport()}
                disabled={reportLoading}
                className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50"
              >
                {reportLoading ? 'Loading…' : 'Apply'}
              </button>
              {reportMonth && (
                <button
                  onClick={() => { setReportMonth(''); setTimeout(() => loadReport(), 0); }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              )}
              <div className="flex-1" />
              <button
                onClick={downloadReportCsv}
                className="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md"
                title="Download CSV for the current scope"
              >
                Download CSV
              </button>
            </div>

            <div className="px-5 py-3 text-sm text-gray-700 border-b border-gray-100 flex gap-6">
              <div><span className="text-gray-500">Total Published:</span> <strong>{reportData?.total_published ?? 0}</strong></div>
              <div><span className="text-gray-500">Months:</span> <strong>{reportData?.months?.length ?? 0}</strong></div>
              {reportData?.scope?.month && (
                <div><span className="text-gray-500">Scope:</span> <strong>{reportData.scope.month}</strong></div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
              {reportError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{reportError}</div>
              )}
              {reportLoading && !reportData && (
                <div className="text-sm text-gray-500">Loading report…</div>
              )}
              {reportData && reportData.months.length === 0 && !reportLoading && (
                <div className="text-sm text-gray-500">No published articles found for this scope.</div>
              )}
              {reportData?.months.map(bucket => (
                <div key={bucket.month} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
                    <div className="font-semibold text-gray-900">{bucket.month}</div>
                    <div className="text-sm text-gray-600">{bucket.count} published</div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm table-fixed">
                      <colgroup>
                        <col style={{ width: '170px' }} />
                        <col style={{ width: '28%' }} />
                        <col style={{ width: '20%' }} />
                        <col />
                        <col style={{ width: '130px' }} />
                      </colgroup>
                      <thead className="bg-white">
                        <tr className="text-left text-xs font-medium text-gray-500 uppercase border-b border-gray-200">
                          <th className="px-3 py-2">Published</th>
                          <th className="px-3 py-2">Title</th>
                          <th className="px-3 py-2">Keyword</th>
                          <th className="px-3 py-2">URL</th>
                          <th className="px-3 py-2">Mode</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {bucket.entries.map(row => (
                          <tr key={`${bucket.month}-${row.entry_id}`} className="hover:bg-gray-50 align-top">
                            <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                              {row.published_at ? new Date(row.published_at).toLocaleString([], {
                                year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                              }) : '—'}
                            </td>
                            <td className="px-3 py-2 text-gray-900 truncate" title={row.title}>{row.title || '—'}</td>
                            <td className="px-3 py-2 text-gray-600 truncate" title={row.keyword}>{row.keyword || '—'}</td>
                            <td className="px-3 py-2 overflow-hidden">
                              {row.wp_url ? (
                                <a
                                  href={row.wp_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline block truncate"
                                  title={row.wp_url}
                                >
                                  {row.wp_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                                </a>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-500 uppercase text-xs whitespace-nowrap">{row.publish_mode || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setReportOpen(false)}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Slack settings modal */}
      {slackOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Slack notifications</h3>
                <p className="text-xs text-gray-500">
                  Get a Slack message with the title, keyword, and URL whenever an article is published.
                </p>
              </div>
              <button
                onClick={() => setSlackOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div className="text-sm">
                <span className="text-gray-600">Current status: </span>
                {slackConfigured ? (
                  <span className="text-purple-700 font-medium">
                    Configured {slackPreview ? `(${slackPreview})` : ''}
                  </span>
                ) : (
                  <span className="text-gray-500">Not configured</span>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slack Incoming Webhook URL
                </label>
                <input
                  type="text"
                  value={slackWebhookInput}
                  onChange={(e) => setSlackWebhookInput(e.target.value)}
                  placeholder="https://hooks.slack.com/services/T…/B…/…"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Create one in Slack: <em>Apps → Incoming Webhooks → Add to channel</em>. Paste the full URL here.
                </p>
              </div>

              {slackMessage && (
                <div
                  className={`text-sm px-3 py-2 rounded-md ${slackMessage.kind === 'ok' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}
                >
                  {slackMessage.text}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
              <div className="flex gap-2">
                {slackConfigured && (
                  <button
                    onClick={() => saveSlackConfig(true)}
                    disabled={slackSaving}
                    className="px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50"
                  >
                    Clear
                  </button>
                )}
                <button
                  onClick={testSlackConfig}
                  disabled={slackTesting || !slackConfigured}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  title={slackConfigured ? 'Send a test message to Slack' : 'Save a webhook first'}
                >
                  {slackTesting ? 'Testing…' : 'Send test'}
                </button>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setSlackOpen(false)}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={() => saveSlackConfig(false)}
                  disabled={slackSaving || !slackWebhookInput.trim()}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md disabled:opacity-50"
                >
                  {slackSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ContentCalendarPage;
